import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDolfinAuth, useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";

/** `"all"` hits the unified `/agent` endpoint across every surface; `"ap"` / `"ar"` / `"books"` the scoped ones. */
export type AgentKind = "ap" | "ar" | "books" | "all";

/** The page a message is sent from; the agent reads "this" and "here" from it. */
export interface AgentPage {
  path: string;
  title: string;
}

export interface ToolAction {
  type: string;
  label: string;
  params?: Record<string, unknown>;
}

/** A record a tool returned. The reply links every exact mention of `label` to it. */
export interface AgentRef {
  type: string;
  id: string;
  label: string;
}

/** A one-click action under the reply: call `tool` with `input` via POST /v1/agent/actions. */
export interface AgentSuggestion {
  id: string;
  tool: string;
  input: Record<string, unknown>;
  label: string;
  reason?: string | null;
  /** The tool changes data, so the user confirms first. */
  confirm: boolean;
  /** The user already ran it; shown as done. */
  done?: boolean;
}

export interface AgentMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  actions?: ToolAction[];
  refs?: AgentRef[];
  suggestions?: AgentSuggestion[];
}

export interface ConversationSummary {
  id: string;
  title: string | null;
  lastMessagePreview: string | null;
  updatedAt: string;
}

interface AgentChatContextValue {
  messages: AgentMessage[];
  isOpen: boolean;
  isStreaming: boolean;
  /** What the agent is doing right now (e.g. "Receivables summary") while a tool runs; null otherwise. */
  status: string | null;
  error: string | null;
  conversationId: string | null;
  recentConversations: ConversationSummary[];
  agent: AgentKind;
  agentName: string;
  toggleChat: () => void;
  /** Opens the panel; a `prompt` is put in the message box for the person to send, not sent. */
  openChat: (prompt?: string) => void;
  /** A prompt waiting to be put in the message box, taken once by the panel. */
  draft: string | null;
  takeDraft: () => string | null;
  closeChat: () => void;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
  loadConversation: (id: string) => Promise<void>;
  refreshConversations: () => Promise<void>;
  /** `open_url` opens a tab here; everything else goes to the host's `onAction`. */
  onAction: (action: ToolAction) => void;
  /** Resolves with the server's error message on failure; on success the chip is marked done or replaced by follow-ups. */
  runSuggestion: (suggestion: AgentSuggestion) => Promise<{ error?: string }>;
}

const AgentChatContext = createContext<AgentChatContextValue | null>(null);

const MAX_MESSAGES = 200;
const RECENT_CONVERSATIONS_LIMIT = 3;

function storageScope(agent: AgentKind, orgId: string | undefined) {
  return `dolfin-agent-${agent}-${orgId ?? "none"}`;
}
function messagesKey(scope: string) {
  return `${scope}-messages`;
}
function conversationKey(scope: string) {
  return `${scope}-conversation`;
}
// One open state for the panel whichever agent it shows, kept under the general agent's key.
function openKey(orgId: string | undefined) {
  return `${storageScope("all", orgId)}-open`;
}

function loadMessages(scope: string): AgentMessage[] {
  try {
    const raw = localStorage.getItem(messagesKey(scope));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-MAX_MESSAGES);
  } catch {
    return [];
  }
}

// Shown when the reply stream drops before the server finished. The server keeps working and saves the
// reply, so reopening the conversation usually shows the answer.
const CONNECTION_LOST =
  "The connection to the assistant was interrupted. Your answer may still arrive — reopen this conversation in a moment to check.";

function loadConversationId(scope: string): string | null {
  return localStorage.getItem(conversationKey(scope));
}

function normaliseActions(raw: unknown): ToolAction[] {
  const candidates: unknown[] = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { actions?: unknown })?.actions)
      ? (raw as { actions: unknown[] }).actions
      : [];
  const actions: ToolAction[] = [];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const { type, label, params } = candidate as {
      type?: unknown;
      label?: unknown;
      params?: unknown;
    };
    if (typeof type !== "string" || typeof label !== "string") continue;
    actions.push({
      type,
      label,
      params:
        params && typeof params === "object" && !Array.isArray(params)
          ? (params as Record<string, unknown>)
          : undefined,
    });
  }
  return actions;
}

function normaliseRefs(raw: unknown): AgentRef[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (r): r is AgentRef =>
      !!r && typeof r.type === "string" && typeof r.id === "string" && typeof r.label === "string",
  );
}

function normaliseSuggestion(raw: unknown): AgentSuggestion | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Partial<AgentSuggestion>;
  if (typeof s.id !== "string" || typeof s.tool !== "string" || typeof s.label !== "string") return null;
  return {
    id: s.id,
    tool: s.tool,
    input: s.input && typeof s.input === "object" ? (s.input as Record<string, unknown>) : {},
    label: s.label,
    reason: typeof s.reason === "string" ? s.reason : null,
    confirm: s.confirm !== false,
    done: s.done === true,
  };
}

function normaliseSuggestions(raw: unknown): AgentSuggestion[] {
  return Array.isArray(raw) ? raw.map(normaliseSuggestion).filter((s): s is AgentSuggestion => s !== null) : [];
}

function extractActionsFromToolResults(toolResultsJson: string | null | undefined): ToolAction[] {
  if (!toolResultsJson) return [];
  try {
    return normaliseActions(JSON.parse(toolResultsJson));
  } catch {
    return [];
  }
}

export interface AgentChatProviderProps {
  /** May change while mounted: the panel then shows that agent's last conversation and stays open or closed. */
  agent?: AgentKind;
  agentName?: string;
  /** Initial open state when the user has no saved preference. Defaults to `false`. */
  defaultOpen?: boolean;
  /** Non-URL tool actions, e.g. `open_bill` → `/bills/:id`. Ignore unknown types. */
  onAction?: (action: ToolAction) => void;
  /** Sent with each message, not saved in the conversation. */
  page?: AgentPage;
  children: React.ReactNode;
}

export function AgentChatProvider({
  agent = "ap",
  agentName = "Dolfin",
  defaultOpen = false,
  onAction,
  page,
  children,
}: AgentChatProviderProps) {
  const { organisationId, authFetch } = useDolfinAuth();
  const api = useDolfinClient();

  const scope = storageScope(agent, organisationId);
  const agentType = agent === "ap" ? "Ap" : agent === "ar" ? "Ar" : agent === "books" ? "Books" : "All";

  const [messages, setMessages] = useState<AgentMessage[]>(() => loadMessages(scope));
  const [conversationId, setConversationId] = useState<string | null>(() =>
    loadConversationId(scope),
  );
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem(openKey(organisationId));
    return saved === null ? defaultOpen : saved === "true";
  });
  const [isStreaming, setIsStreaming] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recentConversations, setRecentConversations] = useState<ConversationSummary[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  // Async work checks it still belongs to the scope on screen before it writes state.
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  // Reload during render when the agent or organisation changes, so the effects that save state below never
  // see one scope's messages under the other's key.
  const [loaded, setLoaded] = useState({ scope, org: organisationId });
  if (loaded.scope !== scope || loaded.org !== organisationId) {
    setLoaded({ scope, org: organisationId });
    setMessages(loadMessages(scope));
    setConversationId(loadConversationId(scope));
    setIsStreaming(false);
    setStatus(null);
    setError(null);
    setRecentConversations([]);
    if (loaded.org !== organisationId) {
      const saved = localStorage.getItem(openKey(organisationId));
      setIsOpen(saved === null ? defaultOpen : saved === "true");
    }
  }

  // A reply in progress keeps running when the scope changes (aborting would stop the server's turn mid-way);
  // it just stops writing here, and isn't the one a later clear or load aborts.
  useEffect(
    () => () => {
      abortRef.current = null;
    },
    [scope],
  );

  // Keep the host's action handler in a ref so the resolved handler stays stable.
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;

  const handleAction = useCallback((action: ToolAction) => {
    if (action.type === "open_url") {
      const url = typeof action.params?.url === "string" ? action.params.url : null;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
    }
    onActionRef.current?.(action);
  }, []);

  const refreshConversations = useCallback(async () => {
    const { data } = await api.GET("/v1/agent/conversations", {
      params: { query: { Limit: RECENT_CONVERSATIONS_LIMIT, AgentType: agentType } },
    });
    const rows = (data?.data ?? [])
      .filter((c): c is typeof c & { id: string } => typeof c.id === "string")
      .map<ConversationSummary>((c) => ({
        id: c.id,
        title: c.title ?? null,
        lastMessagePreview: c.lastMessagePreview ?? null,
        updatedAt: c.updatedAt ?? "",
      }));
    setRecentConversations(rows);
  }, [api, agentType]);

  const fetchConversation = useCallback(
    async (id: string): Promise<AgentMessage[] | null> => {
      const { data, error: apiError } = await api.GET("/v1/agent/conversations/{id}/messages", {
        params: { path: { id } },
      });
      if (apiError || !data) return null;

      return (data.data ?? [])
        .filter((m) => m.role === "User" || m.role === "Assistant")
        .map((m) => {
          // The contract ships actions inline on each row; fall back to parsing
          // tool results for older rows that predate the dedicated field.
          const actions =
            normaliseActions(m.actions).length > 0
              ? normaliseActions(m.actions)
              : extractActionsFromToolResults(m.toolResultsJson);
          const refs = normaliseRefs(m.refs);
          const suggestions = normaliseSuggestions(m.suggestions);
          return {
            id: m.id ?? crypto.randomUUID(),
            role: m.role === "User" ? ("user" as const) : ("assistant" as const),
            content: m.content ?? "",
            timestamp: m.createdAt ? Date.parse(m.createdAt) : Date.now(),
            ...(actions.length > 0 ? { actions } : {}),
            ...(refs.length > 0 ? { refs } : {}),
            ...(suggestions.length > 0 ? { suggestions } : {}),
          };
        })
        .filter(
          (m) =>
            m.role === "user" ||
            m.content.trim() !== "" ||
            (m.actions?.length ?? 0) > 0 ||
            (m.suggestions?.length ?? 0) > 0,
        )
        .sort((a, b) => a.timestamp - b.timestamp);
    },
    [api],
  );

  const loadConversation = useCallback(
    async (id: string) => {
      if (abortRef.current) abortRef.current.abort();
      const loadScope = scopeRef.current;
      setError(null);
      setMessages([]);
      setIsStreaming(true);

      const loaded = await fetchConversation(id);
      if (scopeRef.current !== loadScope) return;
      setIsStreaming(false);
      if (!loaded) {
        setError("Could not load conversation");
        return;
      }
      setMessages(loaded);
      setConversationId(id);
    },
    [fetchConversation],
  );

  // The saved copy can stop part-way through a reply (the panel moved to another agent, or the page
  // reloaded), so show the server's. Silent: if it can't be read, the saved copy stays.
  useEffect(() => {
    const id = loadConversationId(scope);
    if (!id) return;
    fetchConversation(id)
      .then((loaded) => {
        if (loaded && scopeRef.current === scope) setMessages(loaded);
      })
      .catch(() => {});
  }, [scope, fetchConversation]);

  useEffect(() => {
    if (!isOpen) return;
    refreshConversations().catch(() => {});
  }, [isOpen, scope, refreshConversations]);

  useEffect(() => {
    localStorage.setItem(messagesKey(scope), JSON.stringify(messages.slice(-MAX_MESSAGES)));
  }, [messages, scope]);

  useEffect(() => {
    const key = conversationKey(scope);
    if (conversationId) localStorage.setItem(key, conversationId);
    else localStorage.removeItem(key);
  }, [conversationId, scope]);

  useEffect(() => {
    localStorage.setItem(openKey(organisationId), String(isOpen));
  }, [isOpen, organisationId]);

  // A status only means something while a reply is streaming.
  useEffect(() => {
    if (!isStreaming) setStatus(null);
  }, [isStreaming]);

  const runSuggestion = useCallback(
    async (suggestion: AgentSuggestion): Promise<{ error?: string }> => {
      if (!conversationId) return { error: "This conversation has not been saved yet." };
      const { data, error: apiError } = await api.POST("/v1/agent/actions", {
        body: {
          conversationId,
          suggestionId: suggestion.id,
          tool: suggestion.tool,
          input: suggestion.input,
        },
      });
      if (apiError) return { error: apiErrorMessage(apiError, "That action could not be completed.") };
      const followUps = normaliseSuggestions(data?.suggestions);
      setMessages((prev) =>
        prev.map((m) =>
          m.suggestions?.some((s) => s.id === suggestion.id)
            ? {
                ...m,
                suggestions: m.suggestions.flatMap((s) =>
                  s.id !== suggestion.id ? [s] : followUps.length > 0 ? followUps : [{ ...s, done: true }],
                ),
              }
            : m,
        ),
      );
      return {};
    },
    [api, conversationId],
  );

  const toggleChat = useCallback(() => setIsOpen((prev) => !prev), []);
  const [draft, setDraft] = useState<string | null>(null);
  const openChat = useCallback((prompt?: string) => {
    if (prompt) setDraft(prompt);
    setIsOpen(true);
  }, []);
  const takeDraft = useCallback(() => {
    const d = draft;
    if (d !== null) setDraft(null);
    return d;
  }, [draft]);
  const closeChat = useCallback(() => setIsOpen(false), []);

  const clearMessages = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setConversationId(null);
    setIsStreaming(false);
    setError(null);
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      const userMsg: AgentMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content,
        timestamp: Date.now(),
      };
      const assistantMsg: AgentMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "",
        timestamp: Date.now(),
      };
      const assistantId = assistantMsg.id;

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);
      setError(null);

      const abortController = new AbortController();
      abortRef.current = abortController;
      const sendScope = scope;
      const live = () => scopeRef.current === sendScope;

      try {
        const response = await authFetch(agent === "all" ? "/v1/agent" : `/v1/agent/${agent}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ conversationId, message: content, page }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          // ProblemDetails body (no SSE on non-200).
          const problem = (await response.json().catch(() => null)) as
            | { detail?: string; title?: string }
            | null;
          throw new Error(problem?.detail ?? problem?.title ?? `Request failed: ${response.status}`);
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        const appendToAssistant = (chunk: string) => {
          // Text arriving means the tool the status named has finished.
          setStatus(null);
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + chunk } : m)),
          );
        };

        const appendRefs = (refs: AgentRef[]) => {
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m;
              const existing = m.refs ?? [];
              const fresh = refs.filter((r) => !existing.some((e) => e.type === r.type && e.id === r.id));
              return fresh.length > 0 ? { ...m, refs: [...existing, ...fresh] } : m;
            }),
          );
        };

        const appendSuggestion = (suggestion: AgentSuggestion) => {
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m;
              const existing = m.suggestions ?? [];
              // The id is a hash of tool + input, so a repeat from a second tool call is the same chip.
              if (existing.some((s) => s.id === suggestion.id)) return m;
              return { ...m, suggestions: [...existing, suggestion] };
            }),
          );
        };

        const actionKey = (a: ToolAction) =>
          `${a.type}:${a.label}:${JSON.stringify(a.params ?? {})}`;
        const appendAction = (action: ToolAction) => {
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m;
              const existing = m.actions ?? [];
              // Dedupe identical actions (e.g. a tool emitting "View invoices" on repeated calls).
              if (existing.some((a) => actionKey(a) === actionKey(action))) return m;
              return { ...m, actions: [...existing, action] };
            }),
          );
        };

        let finished = false;
        outer: while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          // The panel moved to another agent: read to the end so the server finishes, but write nothing.
          if (!live()) continue;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line (\n\n).
          let sep;
          while ((sep = buffer.indexOf("\n\n")) !== -1) {
            const frame = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);

            let eventName = "message";
            const dataLines: string[] = [];
            for (const line of frame.split("\n")) {
              if (line.startsWith("event:")) eventName = line.slice(6).trim();
              else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
            }
            if (dataLines.length === 0) continue;

            let payload: {
              id?: string;
              label?: string;
              tool?: string;
              content?: string;
              error?: string;
              done?: boolean;
              type?: string;
              params?: Record<string, unknown>;
            };
            let raw: unknown;
            try {
              raw = JSON.parse(dataLines.join("\n"));
            } catch {
              continue;
            }
            if (eventName === "refs") {
              appendRefs(normaliseRefs(raw));
              continue;
            }
            payload = (raw ?? {}) as typeof payload;

            if (eventName === "status") {
              if (typeof payload.label === "string") setStatus(payload.label);
              continue;
            }
            if (eventName === "suggestion") {
              const suggestion = normaliseSuggestion(raw);
              if (suggestion) appendSuggestion(suggestion);
              continue;
            }
            if (eventName === "conversation") {
              if (typeof payload.id === "string") setConversationId(payload.id);
              continue;
            }
            if (eventName === "action") {
              if (typeof payload.type === "string" && typeof payload.label === "string") {
                appendAction({ type: payload.type, label: payload.label, params: payload.params });
              }
              continue;
            }
            if (typeof payload.content === "string") appendToAssistant(payload.content);
            if (payload.error) setError(payload.error);
            if (payload.done) {
              finished = true;
              setIsStreaming(false);
              break outer;
            }
          }
        }

        if (!live()) return;
        // The server always ends a reply with a `done` frame; a stream that closes without one was cut
        // off in transit (a proxy timeout or a dropped connection), not finished.
        if (!finished) setError(CONNECTION_LOST);
        setIsStreaming(false);
        refreshConversations().catch(() => {});
      } catch (e: unknown) {
        if ((e instanceof DOMException && e.name === "AbortError") || !live()) return;
        // fetch and the stream reader reject with a bare TypeError ("Failed to fetch", "network error")
        // when the connection drops — meaningless to a user, so say what happened instead.
        setError(
          e instanceof TypeError
            ? CONNECTION_LOST
            : e instanceof Error
              ? e.message
              : "Something went wrong",
        );
        setIsStreaming(false);
      }
    },
    [agent, authFetch, conversationId, page, refreshConversations, scope],
  );

  return (
    <AgentChatContext.Provider
      value={{
        messages,
        isOpen,
        isStreaming,
        status,
        error,
        conversationId,
        recentConversations,
        agent,
        agentName,
        toggleChat,
        openChat,
        draft,
        takeDraft,
        closeChat,
        sendMessage,
        clearMessages,
        loadConversation,
        refreshConversations,
        onAction: handleAction,
        runSuggestion,
      }}
    >
      {children}
    </AgentChatContext.Provider>
  );
}

export function useAgentChat() {
  const ctx = useContext(AgentChatContext);
  if (!ctx) throw new Error("useAgentChat must be used within <AgentChatProvider>");
  return ctx;
}
